// Iter108 — Strategy payoff-at-expiration sketch (inline SVG).
//
// Renders a SMALL piecewise-linear payoff curve for a defined-risk
// structure so the operator can visualise where the trade makes
// money and where it loses money — without leaving the Strategy
// Scanner card.
//
// Hard contract:
//   * VISUAL SKETCH ONLY.  Not a trade-modelling engine.  Does NOT
//     re-run the evaluator.  Does NOT fetch additional market
//     data.  Pure pure-pure function of the `selected_structure`
//     fields the evaluator already returned.
//   * Read-only.  No persistence.  No network.  No mutation.
//   * No ranking / no winner labelling on the chart.
//
// Supported structures:
//   * 2-leg credit spread (BPCS bull-put / BCCS bear-call).
//   * 4-leg iron condor.
//   * 1-short-call covered call (CFM Shares).
//   * 2-leg diagonal (CFM LEAPS / PMCC) — rough sketch only.
//
// Anything we can't classify renders nothing (the panel falls back
// to the existing inline metric strip — no broken chart shape).
import React from "react";

function _num(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Classify a `selected_structure` payload into a payoff shape the
// SVG can draw.  Returns null when the structure isn't supported.
function _classify(structure) {
  if (!structure || typeof structure !== "object") return null;
  const legs = Array.isArray(structure.legs) ? structure.legs : [];

  // ── CFM Shares covered call (no `legs[]` array) ─────────────
  // Evaluator emits `{ short_call: { strike, … }, call_credit,
  // shares_breakeven, assignment_exit_price }`.  Combined
  // share+short-call payoff at expiration (per-share):
  //   * U ≤ strike → P/L = (U − basis) + call_credit, linear.
  //   * U > strike → capped at (strike − basis) + call_credit.
  //   * Breakeven  → U = basis − call_credit = shares_breakeven.
  if (
    legs.length === 0
    && structure.short_call
    && _num(structure.short_call.strike) != null
    && _num(structure.shares_breakeven) != null
    && _num(structure.call_credit) != null
  ) {
    const strike = _num(structure.short_call.strike);
    const be = _num(structure.shares_breakeven);
    const credit = _num(structure.call_credit);
    const basis = be + credit;
    const maxProfitPS = strike - basis + credit;  // = strike − be
    // Theoretical floor at U=0; we cap the visualised loss at
    // the breakeven distance so the chart shape is meaningful.
    const downsideRange = Math.min(be, be * 0.6);  // visual cap
    const visibleLoss = downsideRange;
    return {
      kind: "CFM_CC",
      maxProfit: maxProfitPS,
      maxLoss: visibleLoss,
      breakevens: [be],
      points: [
        { x: be - downsideRange, y: -visibleLoss },
        { x: be, y: 0 },
        { x: strike, y: maxProfitPS },
        { x: strike + (strike - be) * 0.4, y: maxProfitPS },
      ],
      strikes: [strike],
    };
  }

  if (legs.length === 0) return null;

  // The remaining branches require explicit max_profit / max_loss
  // on the structure payload.
  const maxProfit = _num(structure.max_profit);
  const maxLoss = _num(structure.max_loss);
  if (maxProfit == null || maxLoss == null) return null;

  // Classify by leg count + types.
  const puts = legs.filter(l => l.type === "put");
  const calls = legs.filter(l => l.type === "call");

  // 2-leg BPCS (bull put credit spread): sell put @ short_strike,
  // buy put @ long_strike (short_strike > long_strike).
  if (legs.length === 2 && puts.length === 2 && calls.length === 0) {
    const sellPut = puts.find(l => l.action === "sell");
    const buyPut = puts.find(l => l.action === "buy");
    if (!sellPut || !buyPut) return null;
    const shortStrike = _num(sellPut.strike);
    const longStrike = _num(buyPut.strike);
    if (shortStrike == null || longStrike == null) return null;
    if (shortStrike <= longStrike) return null;
    const be = _num(structure.breakeven);
    return {
      kind: "BPCS",
      maxProfit, maxLoss, breakevens: be == null ? [] : [be],
      // Payoff at expiration:
      //   underlying ≤ longStrike  → -maxLoss
      //   underlying = breakeven   → 0
      //   underlying ≥ shortStrike → +maxProfit
      points: [
        { x: longStrike, y: -maxLoss },
        be != null ? { x: be, y: 0 } : null,
        { x: shortStrike, y: maxProfit },
      ].filter(Boolean),
      strikes: [longStrike, shortStrike],
    };
  }

  // 2-leg BCCS (bear call credit spread): sell call @ short_strike,
  // buy call @ long_strike (long_strike > short_strike).
  if (legs.length === 2 && calls.length === 2 && puts.length === 0) {
    const sellCall = calls.find(l => l.action === "sell");
    const buyCall = calls.find(l => l.action === "buy");
    if (!sellCall || !buyCall) return null;
    const shortStrike = _num(sellCall.strike);
    const longStrike = _num(buyCall.strike);
    if (shortStrike == null || longStrike == null) return null;
    if (longStrike <= shortStrike) return null;
    const be = _num(structure.breakeven);
    return {
      kind: "BCCS",
      maxProfit, maxLoss, breakevens: be == null ? [] : [be],
      points: [
        { x: shortStrike, y: maxProfit },
        be != null ? { x: be, y: 0 } : null,
        { x: longStrike, y: -maxLoss },
      ].filter(Boolean),
      strikes: [shortStrike, longStrike],
    };
  }

  // 4-leg Iron Condor: 2 puts + 2 calls.
  if (legs.length === 4 && puts.length === 2 && calls.length === 2) {
    const longPut = puts.find(l => l.action === "buy");
    const shortPut = puts.find(l => l.action === "sell");
    const shortCall = calls.find(l => l.action === "sell");
    const longCall = calls.find(l => l.action === "buy");
    if (!longPut || !shortPut || !shortCall || !longCall) return null;
    const lp = _num(longPut.strike), sp = _num(shortPut.strike);
    const sc = _num(shortCall.strike), lc = _num(longCall.strike);
    if ([lp, sp, sc, lc].some(v => v == null)) return null;
    if (!(lp < sp && sp <= sc && sc < lc)) return null;
    const bes = Array.isArray(structure.breakeven)
      ? structure.breakeven.map(_num).filter(v => v != null)
      : structure.breakeven != null ? [_num(structure.breakeven)] : [];
    return {
      kind: "IRON_CONDOR",
      maxProfit, maxLoss, breakevens: bes,
      points: [
        { x: lp, y: -maxLoss },
        bes.length === 2 ? { x: bes[0], y: 0 } : null,
        { x: sp, y: maxProfit },
        { x: sc, y: maxProfit },
        bes.length === 2 ? { x: bes[1], y: 0 } : null,
        { x: lc, y: -maxLoss },
      ].filter(Boolean),
      strikes: [lp, sp, sc, lc],
    };
  }

  // Covered call (CFM Shares): 1 short call, evaluator emits only
  // the call leg; the share position is implicit.  Sketch: linear
  // gain below the strike (capped slope), flat-capped above.
  if (legs.length === 1 && calls.length === 1) {
    const shortCall = calls.find(l => l.action === "sell");
    if (!shortCall) return null;
    const strike = _num(shortCall.strike);
    if (strike == null) return null;
    const be = _num(structure.breakeven);
    return {
      kind: "CFM_CC",
      maxProfit, maxLoss, breakevens: be == null ? [] : [be],
      points: [
        be != null ? { x: be, y: -maxLoss } : null,
        { x: strike, y: maxProfit },
        { x: strike * 1.15, y: maxProfit },
      ].filter(Boolean),
      strikes: [strike],
    };
  }

  // 2-leg diagonal (CFM LEAPS / PMCC): rough sketch — treat like a
  // capped-upside spread for visualisation.
  if (legs.length === 2 && calls.length === 2) {
    const shortCall = calls.find(l => l.action === "sell");
    const longCall = calls.find(l => l.action === "buy");
    if (!shortCall || !longCall) return null;
    const sc = _num(shortCall.strike), lc = _num(longCall.strike);
    if (sc == null || lc == null) return null;
    const be = _num(structure.breakeven);
    return {
      kind: "PMCC",
      maxProfit, maxLoss, breakevens: be == null ? [] : [be],
      points: [
        be != null ? { x: be, y: -maxLoss } : null,
        { x: sc, y: maxProfit },
      ].filter(v => v),
      strikes: [lc, sc],
    };
  }

  // CFM-Shares (no `legs[]` shape) is handled UP-TOP, before the
  // legs.length === 0 guard.  Anything we can't classify above
  // falls through here.
  return null;
}

export function PayoffChart({
  structure, spot = null, width = 200, height = 80,
  testid = "payoff-chart",
}) {
  const cls = _classify(structure);
  if (!cls || cls.points.length < 2) return null;

  // Build X-axis domain — expand a bit beyond the strikes so the
  // operator sees where the curve flattens.
  const xs = cls.points.map(p => p.x).concat(cls.strikes || []);
  if (spot != null && Number.isFinite(spot)) xs.push(spot);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const padX = Math.max((maxX - minX) * 0.18, 0.5);
  const xMin = minX - padX;
  const xMax = maxX + padX;

  // Y-axis: symmetric around 0 so the zero line lives mid-chart.
  const maxAbsY = Math.max(
    Math.abs(cls.maxProfit), Math.abs(cls.maxLoss), 1,
  );
  const yMin = -maxAbsY * 1.18;
  const yMax = maxAbsY * 1.18;

  const padding = { left: 24, right: 8, top: 6, bottom: 14 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const xToPx = x => padding.left + ((x - xMin) / (xMax - xMin)) * innerW;
  const yToPx = y => padding.top + ((yMax - y) / (yMax - yMin)) * innerH;

  // Build the payoff polyline.  Extend the first and last segments
  // out to the chart edges so the curve plateaus visually.
  const sortedPoints = [...cls.points].sort((a, b) => a.x - b.x);
  const first = sortedPoints[0];
  const last = sortedPoints[sortedPoints.length - 1];
  const polyPoints = [
    { x: xMin, y: first.y },
    ...sortedPoints,
    { x: xMax, y: last.y },
  ];
  const polylineAttr = polyPoints
    .map(p => `${xToPx(p.x).toFixed(2)},${yToPx(p.y).toFixed(2)}`)
    .join(" ");

  // Build separate green (gain) and red (loss) overlay polylines
  // by linearly interpolating across the zero crossings.
  const gainPts = [];
  const lossPts = [];
  for (let i = 0; i < polyPoints.length; i += 1) {
    const p = polyPoints[i];
    if (p.y >= 0) gainPts.push(p);
    if (p.y <= 0) lossPts.push(p);
    if (i + 1 < polyPoints.length) {
      const q = polyPoints[i + 1];
      if ((p.y > 0 && q.y < 0) || (p.y < 0 && q.y > 0)) {
        const t = -p.y / (q.y - p.y);
        const zx = p.x + t * (q.x - p.x);
        gainPts.push({ x: zx, y: 0 });
        lossPts.push({ x: zx, y: 0 });
      }
    }
  }
  const gainAttr = gainPts
    .map(p => `${xToPx(p.x).toFixed(2)},${yToPx(p.y).toFixed(2)}`)
    .join(" ");
  const lossAttr = lossPts
    .map(p => `${xToPx(p.x).toFixed(2)},${yToPx(p.y).toFixed(2)}`)
    .join(" ");

  // Zero-line at y=0.
  const zeroY = yToPx(0);
  // Right & left ticks for max-profit / max-loss labels.
  const yMaxLabel = "+$" + cls.maxProfit.toFixed(0);
  const yMinLabel = "-$" + Math.abs(cls.maxLoss).toFixed(0);

  return (
    <svg
      data-testid={`${testid}-${cls.kind}`}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={`${cls.kind} payoff at expiration`}
      style={{ display: "block", overflow: "visible" }}
    >
      {/* Frame */}
      <rect
        x={padding.left} y={padding.top}
        width={innerW} height={innerH}
        fill="#0A0A0E" stroke="#26262B" strokeWidth="0.5"
      />
      {/* Zero baseline */}
      <line
        x1={padding.left} x2={padding.left + innerW}
        y1={zeroY} y2={zeroY}
        stroke="#3F3F47" strokeWidth="0.6" strokeDasharray="2 2"
      />
      {/* Strike markers */}
      {(cls.strikes || []).map((sx, i) => (
        <line
          key={`strike-${i}`}
          x1={xToPx(sx)} x2={xToPx(sx)}
          y1={padding.top} y2={padding.top + innerH}
          stroke="#22D3FF" strokeWidth="0.4" strokeDasharray="1 2"
          opacity={0.6}
        />
      ))}
      {/* Spot marker (white) */}
      {spot != null && Number.isFinite(spot) && (
        <line
          x1={xToPx(spot)} x2={xToPx(spot)}
          y1={padding.top} y2={padding.top + innerH}
          stroke="#E5E5E7" strokeWidth="0.6"
        />
      )}
      {/* Breakeven markers (yellow dot at y=0) */}
      {(cls.breakevens || []).map((be, i) => (
        <circle
          key={`be-${i}`}
          cx={xToPx(be)} cy={zeroY}
          r="1.6" fill="#FFB800"
        />
      ))}
      {/* Loss fill (red) */}
      {lossPts.length >= 2 && (
        <polyline
          points={lossAttr}
          fill="none" stroke="#FF3366" strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )}
      {/* Gain fill (green) */}
      {gainPts.length >= 2 && (
        <polyline
          points={gainAttr}
          fill="none" stroke="#00FF88" strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )}
      {/* Y-axis labels: max profit / max loss */}
      <text
        x={padding.left - 2} y={padding.top + 6}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="7" fill="#00FF88" textAnchor="end"
      >{yMaxLabel}</text>
      <text
        x={padding.left - 2} y={padding.top + innerH - 1}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="7" fill="#FF3366" textAnchor="end"
      >{yMinLabel}</text>
      {/* Kind label */}
      <text
        x={padding.left + innerW - 2} y={padding.top + 7}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="6" fill="#5F5F66" textAnchor="end"
        letterSpacing="0.1em"
      >P/L @ EXP</text>
    </svg>
  );
}

export default PayoffChart;
